import {JobDetails} from '../JobDetail/utilTypes';
import {
  PUBLIC_QUEUE_RAMP_KEY,
  TEST_JOBS_PROJECT_NAME,
  TEST_PUBLIC_QUEUE_ACCESS_ENTITY,
  TEST_PUBLIC_QUEUE_ENTITY,
  TEST_PUBLIC_QUEUE_NAME,
  WANDB_ENTITY_NAME,
  WANDB_JOBS_PROJECT_NAME,
  WANDB_PUBLIC_QUEUE_NAME,
} from './constants';

export type OrderNode = {
  path: string | string[];
  nestedPaths?: OrderNode[];
};

export type InputOrderSpec = OrderNode[];

export type PublicJobInfo = {
  entityName: string;
  projectName: string;
  jobName: string;
  leaderboardName: string;
  leaderboardConfigOverrideKey?: string;
  inputOrderSpec: InputOrderSpec;
};

const evalApiHostedModelInputOrderSpec: InputOrderSpec = [
  {
    path: 'model',
    nestedPaths: [
      {path: 'base_url'},
      {path: 'model_name'},
      {path: 'api_key_var'},
    ],
  },
  {
    path: 'tasks',
  },
  {
    path: 'hf_token',
  },
  {
    path: 'scorer_api_key',
  },
  {
    path: 'limit',
  },
  {
    path: 'create_leaderboard',
  },
];

const evalModelCheckpointInputOrderSpec: InputOrderSpec = [
  {
    path: 'artifact_path',
  },
  {
    path: 'tasks',
  },
  {
    path: 'hf_token',
  },
  {
    path: 'scorer_api_key',
  },
  {
    path: 'limit',
  },
  {
    path: 'create_leaderboard',
  },
];

// public jobs for production CoreWeave queue (wandb/jobs)
export const PUBLIC_JOBS_INFO: Record<string, PublicJobInfo> = {
  'Evaluate API-hosted model': {
    entityName: WANDB_ENTITY_NAME,
    projectName: WANDB_JOBS_PROJECT_NAME,
    jobName: 'Evaluate API-hosted model',
    leaderboardName: 'Inspect-AI-Leaderboard',
    leaderboardConfigOverrideKey: 'create_leaderboard',
    inputOrderSpec: evalApiHostedModelInputOrderSpec,
  },
  'Evaluate model checkpoint': {
    entityName: WANDB_ENTITY_NAME,
    projectName: WANDB_JOBS_PROJECT_NAME,
    jobName: 'Evaluate model checkpoint',
    leaderboardName: 'Inspect-AI-Leaderboard',
    leaderboardConfigOverrideKey: 'create_leaderboard',
    inputOrderSpec: evalModelCheckpointInputOrderSpec,
  },
};

export const PUBLIC_JOBS = Object.keys(PUBLIC_JOBS_INFO);

export const TEST_PUBLIC_JOBS_INFO: Record<string, PublicJobInfo> = {
  'Test_evaluate_API-hosted_model': {
    entityName: WANDB_ENTITY_NAME,
    projectName: TEST_JOBS_PROJECT_NAME,
    jobName: 'Test_evaluate_API-hosted_model',
    leaderboardName: 'Inspect-AI-Leaderboard',
    leaderboardConfigOverrideKey: 'create_leaderboard',
    inputOrderSpec: evalApiHostedModelInputOrderSpec,
  },
  Test_evaluate_model_checkpoint: {
    entityName: WANDB_ENTITY_NAME,
    projectName: TEST_JOBS_PROJECT_NAME,
    jobName: 'Test_evaluate_model_checkpoint',
    leaderboardName: 'Inspect-AI-Leaderboard',
    leaderboardConfigOverrideKey: 'create_leaderboard',
    inputOrderSpec: evalModelCheckpointInputOrderSpec,
  },
  'Test_evaluate_API-hosted_model_qa': {
    entityName: WANDB_ENTITY_NAME,
    projectName: TEST_JOBS_PROJECT_NAME,
    jobName: 'Test_evaluate_API-hosted_model_qa',
    leaderboardName: 'Inspect-AI-Leaderboard',
    leaderboardConfigOverrideKey: 'create_leaderboard',
    inputOrderSpec: evalApiHostedModelInputOrderSpec,
  },
};

export const TEST_PUBLIC_JOBS = Object.keys(TEST_PUBLIC_JOBS_INFO);

export type PublicQueueConfig = {
  queueName: string;
  entityName: string;
  projectName: string;
  validJobs: PublicJobInfo[];

  // todo: this will be integrated into the usePublicQueueAccess hook
  // to check multiple rampKeys when there are new public queues added.
  accessControl: {
    type: 'org-ramp' | 'entity-membership';
    rampKey?: string; // For org-ramp type
    requiredEntity?: string; // For entity-membership type
  };
};

export const PUBLIC_QUEUES: Record<string, PublicQueueConfig> = {
  [WANDB_PUBLIC_QUEUE_NAME]: {
    queueName: WANDB_PUBLIC_QUEUE_NAME,
    entityName: WANDB_ENTITY_NAME,
    projectName: WANDB_JOBS_PROJECT_NAME,
    validJobs: [
      PUBLIC_JOBS_INFO['Evaluate API-hosted model'],
      PUBLIC_JOBS_INFO['Evaluate model checkpoint'],
      TEST_PUBLIC_JOBS_INFO['Test_evaluate_API-hosted_model'],
      TEST_PUBLIC_JOBS_INFO['Test_evaluate_model_checkpoint'],
      TEST_PUBLIC_JOBS_INFO['Test_evaluate_API-hosted_model_qa'],
    ],

    accessControl: {
      type: 'org-ramp',
      rampKey: PUBLIC_QUEUE_RAMP_KEY,
    },
  },
  [TEST_PUBLIC_QUEUE_NAME]: {
    queueName: TEST_PUBLIC_QUEUE_NAME,
    entityName: TEST_PUBLIC_QUEUE_ENTITY,
    projectName: TEST_JOBS_PROJECT_NAME,
    validJobs: [
      // Allow wandb/jobs jobs to be run on the test queue so we can test out
      // queue config or additional services config changes
      PUBLIC_JOBS_INFO['Evaluate API-hosted model'],
      PUBLIC_JOBS_INFO['Evaluate model checkpoint'],
      TEST_PUBLIC_JOBS_INFO['Test_evaluate_API-hosted_model'],
      TEST_PUBLIC_JOBS_INFO['Test_evaluate_model_checkpoint'],
      TEST_PUBLIC_JOBS_INFO['Test_evaluate_API-hosted_model_qa'],
    ],

    accessControl: {
      type: 'entity-membership',
      requiredEntity: TEST_PUBLIC_QUEUE_ACCESS_ENTITY,
    },
  },
};

export const getPublicQueueConfig = (
  queueName: string
): PublicQueueConfig | undefined => {
  return PUBLIC_QUEUES[queueName];
};

// Check if a job is valid for a specific public queue
export const isJobValidForPublicQueue = (
  queueName: string,
  jobName: string,
  projectName: string,
  entityName: string
): boolean => {
  const config = getPublicQueueConfig(queueName);
  if (!config) {
    return false;
  }

  return config.validJobs.some(
    job =>
      job.jobName === jobName &&
      job.projectName === projectName &&
      job.entityName === entityName
  );
};

export const isJobValidForSomePublicQueue = (job: JobDetails): boolean => {
  return Object.values(PUBLIC_QUEUES).some(queue =>
    isJobValidForPublicQueue(
      queue.queueName,
      job.name,
      job.projectName,
      job.entityName
    )
  );
};
